import { useEffect, useState } from "react";

import type { UseFormReturn } from "react-hook-form";

import type { UserCourseLesson } from "api/schemas/UserCourse";
import type {
  DurationWeeks,
  PatientTreatmentSchedule,
  PostTreatmentSchedulePayload,
  TreatmentScheduleTemplate,
} from "api/services/treatment/schemas/Treatment";
import { useApiClient } from "api/useApiClient";
import { reportError } from "utils/errorReporting";

import type { TimelineActivity, TimelineWeek, TreatmentEditorSettings } from "../types";
import { convertScheduleExercisesToTimelineActivities, getDefaultValues } from "../utils";

interface Patient {
  id: number;
  currentWeek: number;
}

interface UseTreatmentScheduleLoaderParams {
  editorSettings: TreatmentEditorSettings;
  form: UseFormReturn<PostTreatmentSchedulePayload>;
  patient: Patient | undefined;
  treatmentScheduleID: number | undefined;
  resetHistory: (weeks: TimelineWeek[]) => void;
  setLessons: React.Dispatch<React.SetStateAction<UserCourseLesson[]>>;
  setWeeks: React.Dispatch<React.SetStateAction<TimelineWeek[]>>;
}

export const useTreatmentScheduleLoader = ({
  editorSettings,
  form,
  patient,
  treatmentScheduleID,
  resetHistory,
  setLessons,
  setWeeks,
}: UseTreatmentScheduleLoaderParams) => {
  const apiClient = useApiClient();
  const [isFetchingSchedule, setIsFetchingSchedule] = useState(false);

  const { context, mode } = editorSettings;

  const initializeEmptyWeeks = (length: DurationWeeks) => {
    const actualLength = length === null ? 12 : length;
    const newWeeks: TimelineWeek[] = Array.from({ length: actualLength }, (_, index) => ({
      weekNumber: index + 1,
      activities: [],
      expanded: index < 4,
    }));
    setWeeks(newWeeks);
    resetHistory(newWeeks);
  };

  const loadFromTreatmentSchedule = (data: PatientTreatmentSchedule | TreatmentScheduleTemplate) => {
    const maxWeek = ("calculated_duration_weeks" in data ? data.calculated_duration_weeks : data.duration_weeks) || 12;
    form.setValue("duration_weeks", maxWeek);

    const weekMap = new Map<number, TimelineActivity[]>();

    data.exercises.forEach((exercise, index) => {
      exercise.weeks.forEach(week => {
        if (!weekMap.has(week)) {
          weekMap.set(week, []);
        }

        const activities = weekMap.get(week);
        if (activities) {
          activities.push(convertScheduleExercisesToTimelineActivities({ exercise, index, weekNumber: week }));
        }
      });
    });

    const loadedLessons: UserCourseLesson[] = (data.lessons || [])
      .sort((a, b) => a.position - b.position)
      .map(lesson => ({
        id: lesson.text_lesson_id,
        image_url: lesson.image_url,
        title: lesson.title || `Lesson ${lesson.text_lesson_id}`,
        parts_count: 0,
        target_joints: [],
        creator_id: null,
      }));

    setLessons(loadedLessons);

    const currentWeek = patient?.currentWeek || 1;
    const newWeeks: TimelineWeek[] = Array.from({ length: maxWeek }, (_, index) => {
      const weekNumber = index + 1;
      const isCurrentOrNext3Weeks = weekNumber >= currentWeek && weekNumber <= currentWeek + 3;

      return {
        weekNumber,
        activities: weekMap.get(weekNumber) || [],
        expanded: isCurrentOrNext3Weeks,
      };
    });

    setWeeks(newWeeks);
    form.reset(getDefaultValues(data));
    resetHistory(newWeeks);
  };

  const loadTreatmentSchedule = async ({ patientID, templateID }: { patientID?: number; templateID?: number }) => {
    setIsFetchingSchedule(true);
    try {
      if (patientID) {
        const treatmentScheduleData = await apiClient.getPatientTreatmentSchedule(patientID);

        if (treatmentScheduleData) {
          loadFromTreatmentSchedule(treatmentScheduleData);
        }
      } else if (templateID) {
        const treatmentScheduleData = await apiClient.getTreatmentSchedule(templateID);

        if (treatmentScheduleData) {
          loadFromTreatmentSchedule(treatmentScheduleData);
        }
      }
    } catch (error) {
      reportError("useTreatmentScheduleLoader: Failed to load treatment schedule", error as Error);
      throw error;
    } finally {
      setIsFetchingSchedule(false);
    }
  };

  useEffect(() => {
    const durationWeeks = form.getValues("duration_weeks");

    const load = async () => {
      if (context === "library") {
        if (treatmentScheduleID) {
          await loadTreatmentSchedule({ templateID: treatmentScheduleID });
        } else {
          initializeEmptyWeeks(durationWeeks);
        }
        return;
      }

      if (context === "patient" && patient?.id) {
        try {
          if (treatmentScheduleID) {
            await loadTreatmentSchedule({ templateID: treatmentScheduleID });
          } else {
            await loadTreatmentSchedule({ patientID: patient.id });
          }
        } catch {
          initializeEmptyWeeks(durationWeeks);
        }
      }
    };

    load();
  }, [context, mode, patient?.id, treatmentScheduleID]);

  return {
    isFetchingSchedule,
    loadTreatmentSchedule,
  };
};
